import { Box, Flex, Text, Tooltip } from '@mantine/core';

export type SourcesRow = {
  name: string;
  uniques: number;
  pct: number;
  is_other?: boolean;
  // Referrers rolled into a bucket row (Organic), shown on hover.
  members?: { name: string; uniques: number }[];
  kind?: 'organic' | 'direct';
};

export type SourcesData = {
  eligible: boolean;
  rows: SourcesRow[];
  total_uniques: number;
  top_n: number;
  other_rule: string;
  other_sources: { name: string; uniques: number }[];
};

type SourcesChartProps = {
  data: SourcesData | null;
  isDarkTheme?: boolean;
};

// Curated hues first, golden angle beyond, same scheme the contribution chart uses.
const BASE_HUES = [217, 145, 265, 38, 190, 330];
const hueFor = (index: number) =>
  index < BASE_HUES.length ? BASE_HUES[index] : Math.round((217 + index * 137.508) % 360);

const num = (value: number) => Math.round(value).toLocaleString();

const KIND_HOVER = {
  organic: 'Organic: not from an ad, sent by another site.',
  direct: 'Direct: no site was recorded. Typed the address, used a bookmark, or clicked a link in an email, text or app.',
};

// Rounded axis step so the gridlines land on readable numbers.
const niceStep = (max: number) => {
  if (max <= 0) { return 1; }
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const mult = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return mult * mag;
};

export function SourcesChart({ data, isDarkTheme = false }: SourcesChartProps) {
  const muted = isDarkTheme ? '#909296' : '#868e96';
  const gridLine = isDarkTheme ? '#373a40' : '#e9ecef';
  const strongText = isDarkTheme ? '#e9ecef' : '#343a40';

  if (!data || !data.eligible || data.total_uniques <= 0 || data.rows.length === 0) {
    return (
      <Box py={48}>
        <Text size="sm" color="dimmed" align="center">
          Selected Campaigns has no data
        </Text>
      </Box>
    );
  }

  const { rows, total_uniques: total, other_rule: otherRule, other_sources: otherSources } = data;
  // Bars scale against the largest source, so the smallest ones stay visible.
  const max = Math.max(...rows.map((r) => r.uniques));
  const step = niceStep(max);
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) { ticks.push(v); }

  const gridBackground = {
    backgroundImage: `linear-gradient(to right, ${gridLine} 1px, transparent 1px)`,
    backgroundSize: `${(step / max) * 100}% 100%`,
    backgroundPosition: 'left top',
  };

  const labelCol = 160;
  const gridTemplate = `${labelCol}px minmax(220px, 1fr)`;

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <Box sx={{ minWidth: labelCol + 320, display: 'grid', gridTemplateColumns: gridTemplate }}>
          <Box />
          <Flex align="flex-end" justify="space-between" pb={4} pr={8}>
            <Text size="xs" weight={700} sx={{ color: muted, letterSpacing: 0.6 }}>
              UNIQUES
            </Text>
            <Text size="xs" weight={700} sx={{ color: strongText }}>
              {num(total)} total {total === 1 ? 'unique' : 'uniques'}
            </Text>
          </Flex>

          {rows.map((row, index) => {
            const hue = hueFor(index);
            const isOther = !!row.is_other;
            const members = Array.isArray(row.members) ? row.members : [];
            const hoverList = isOther && otherSources.length > 0 ? otherSources : members;
            const hoverTitle = isOther ? otherRule : (row.kind ? KIND_HOVER[row.kind] : `${row.name} covers these referrers.`);
            const hasHover = hoverList.length > 0 || !!row.kind;
            const solid = isOther
              ? (isDarkTheme ? '#5c5f66' : '#adb5bd')
              : `hsl(${hue}, 62%, ${isDarkTheme ? 52 : 46}%)`;
            const barPct = (row.uniques / max) * 100;
            // The value only goes inside the bar when the bar is wide enough to hold it.
            const showInside = barPct >= 22;
            const label = `${num(row.uniques)} (${Number(row.pct).toFixed(2)}%)`;

            const bar = (
              <Flex align="center" sx={{ minHeight: 46, ...gridBackground }}>
                <Flex align="center" sx={{ width: '100%' }}>
                  <Flex
                    align="center"
                    justify="flex-end"
                    pr={showInside ? 10 : 0}
                    sx={{
                      width: `${barPct}%`,
                      minWidth: 6,
                      height: 30,
                      borderRadius: 5,
                      backgroundColor: solid,
                    }}
                  >
                    {showInside && (
                      <Text size="sm" weight={700} sx={{ color: '#fff', whiteSpace: 'nowrap' }}>
                        {label}
                      </Text>
                    )}
                  </Flex>
                  {!showInside && (
                    <Text size="sm" weight={700} pl={8} sx={{ color: strongText, whiteSpace: 'nowrap' }}>
                      {label}
                    </Text>
                  )}
                </Flex>
              </Flex>
            );

            return [
              <Flex
                key={`l-${row.name}-${index}`}
                direction="column"
                justify="center"
                align="flex-end"
                pr="sm"
                sx={{ minHeight: 46 }}
              >
                <Text size="sm" weight={600} align="right" lineClamp={2} title={row.name}>
                  {row.name}
                </Text>
                {hoverList.length > 0 && (
                  <Text size="xs" sx={{ color: muted }}>
                    {hoverList.length} source{hoverList.length === 1 ? '' : 's'}
                  </Text>
                )}
              </Flex>,

              hasHover ? (
                <Tooltip
                  key={`b-${row.name}-${index}`}
                  multiline
                  withArrow
                  position="top"
                  width={300}
                  transitionProps={{ transition: 'pop', duration: 150 }}
                  label={
                    <Box>
                      <Text size="xs" weight={700} mb={hoverList.length > 0 ? 4 : 0}>{hoverTitle}</Text>
                      {hoverList.map((s, i) => (
                        <Text key={`${s.name}-${i}`} size="xs">{`${s.name} — ${num(s.uniques)}`}</Text>
                      ))}
                    </Box>
                  }
                >
                  <Box sx={{ cursor: 'default' }}>{bar}</Box>
                </Tooltip>
              ) : (
                <Box key={`b-${row.name}-${index}`}>{bar}</Box>
              ),
            ];
          })}

          {/* Axis */}
          <Box />
          <Box sx={{ position: 'relative', height: 26, borderTop: `1px solid ${gridLine}` }}>
            {ticks.map((t) => (
              <Text
                key={`t-${t}`}
                size="xs"
                sx={{
                  position: 'absolute',
                  left: `${(t / max) * 100}%`,
                  transform: t >= max ? 'translateX(-100%)' : 'translateX(-50%)',
                  paddingTop: 4,
                  color: muted,
                }}
              >
                {num(t)}
              </Text>
            ))}
          </Box>
        </Box>
      </Box>

      <Text size="xs" color="dimmed" mt="md">
        Each visitor is counted once, at the source that first brought them to the
        landing page. A later visit from a different source does not add or move them.
        Sorted highest to lowest.
        {otherSources.length > 0 ? ` ${otherRule}` : ''}
        {' '}Hover Organic, Direct or any bar showing a source count to see what it contains.
      </Text>
    </Box>
  );
}
